import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import './GeminiChat.css';
import './ArticleWithKeywords.css';

const GeminiChat = forwardRef(({ article, presetInput, initialKeywords }, ref) => {
  // const GeminiChat = forwardRef(({ article }, ref) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [dynamicPrompts, setDynamicPrompts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const endRef = useRef(null);
  const [promptsLoaded, setPromptsLoaded] = useState(false);
  const [quotedPreview, setQuotedPreview] = useState('');


  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (presetInput) {
      setInput(presetInput);
      setIsExpanded(true);
    }
  }, [presetInput]);

  useEffect(() => {
    const fetchDynamicPrompts = async () => {
      try {
        let keywords = initialKeywords;

        // Only fetch keywords if not provided
        if (!keywords || keywords.length === 0) {
          console.log("GeminiChat: No keywords provided, fetching them...");
          const keywordsRes = await fetch('/api/extract-keywords', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              articleContent: article.content,
              articleTitle: article.title,
            }),
          });
          if (!keywordsRes.ok) throw new Error("Keywords fetch failed");
          const data = await keywordsRes.json();
          keywords = data.keywords;
        } else {
          console.log("GeminiChat: Using provided keywords:", keywords.length);
        }

        const promptsRes = await fetch('/api/initial-prompts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ keywords, articleTitle: article.title }),
        });

        const promptsData = await promptsRes.json();
        console.log('[GeminiChat] initial-prompts response:', promptsData);
        const { questions } = promptsData;
        if (questions?.length > 0) {
          setDynamicPrompts(questions);
        }

        setPromptsLoaded(true);
      } catch (err) {
        console.error('Prompt generation failed:', err);
        setPromptsLoaded(true);
      }
    };

    fetchDynamicPrompts();
  }, [article, initialKeywords]);

  const sendMessage = async (customMessage) => {
    const userMessage = (customMessage || input).trim();
    if (!userMessage) return;

    const fullMessage = quotedPreview
      ? `"${quotedPreview}"\n\n${userMessage}`
      : userMessage;

    setQuotedPreview(null)
    setHasStarted(true);
    setLoading(true);
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: fullMessage }]);

    try {
      const context = article
        ? `You are having a conversation about the following article:\n\nTitle: ${article.title}\nAuthor: ${article.author}\nDate: ${article.date}\nContent: ${article.content.substring(0, 2000)}...\n\nPlease answer questions based on this article context.`
        : '';

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          context,
          history: messages
        })
      });

      const data = await response.json();
      console.log('[GeminiChat] chat response:', data);
      const assistantResponse = data.response;

      setMessages(prev => [...prev, { role: 'model', text: assistantResponse }]);


      const followupRes = await fetch('/api/followup-prompts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          latestResponse: assistantResponse,
          topic: 'NBA Finals'
        })
      });

      const followupData = await followupRes.json();
      setSuggestions(followupData.questions || []);
    } catch (err) {
      console.error(err);
      setMessages(prev => [...prev, { role: 'model', text: 'Sorry – something went wrong. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  useImperativeHandle(ref, () => ({
    summarizeText: async (text) => {
      if (!text || text.trim().split(/\s+/).length < 6) {
        setMessages(prev => [
          ...prev,
          { role: 'model', text: 'Please select at least 6 words to summarize.' }
        ]);
        return;
      }

      const userMessage = `Summarize this:\n\n"${text}"`;
      await sendMessage(userMessage);
    },

    askLima: (text) => {
      if (!text || text.trim().length < 6) {
        setMessages(prev => [
          ...prev,
          { role: 'model', text: 'Please highlight at least 6 words to ask about.' }
        ]);
        return;
      }

      setIsExpanded(true);
      setHasStarted(true);
      setQuotedPreview(text);
    }

  }));

  return (
    <div className={`gemini-chat-container ${isExpanded ? 'expanded' : ''}`}>
      <div className={`gemini-chat ${hasStarted ? 'expanded' : ''}`}>
        {!hasStarted && (
          <>
            <div className="chat-search-row">
              <h4 className="chat-prompt-heading">Curious about something?</h4>
              <form className="chat-search-form" onSubmit={(e) => {
                e.preventDefault();
                sendMessage();
              }}>
                <input
                  type="text"
                  className="chat-search-input"
                  placeholder="Ask a question..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={loading}
                />
              </form>
            </div>

            <div className="chat-prompts-buttons">
              {!promptsLoaded && <div className="chat-loading">Loading suggestions...</div>}
              {promptsLoaded && dynamicPrompts.length === 0 && (
                <div className="chat-loading">Suggestions failed to generate.</div>
              )}
              {promptsLoaded && dynamicPrompts.length > 0 && (
                dynamicPrompts.map((prompt, idx) => (
                  <button key={idx} className="chat-prompt-button" onClick={() => sendMessage(prompt)}>
                    {prompt}
                  </button>
                ))
              )}
            </div>
          </>
        )}

        {hasStarted && (
          <>
            <div className="chat-window">
              {messages.length === 0 ? (
                <div className="chat-welcome">
                  <p>Ask me anything about this article!</p>
                </div>
              ) : (
                messages.map((m, i) => (
                  <div key={i} className={`msg ${m.role}`}>
                    <span>{m.text}</span>
                  </div>
                ))
              )}
              <div ref={endRef} />
            </div>

            {!loading && suggestions.length > 0 && (
              <div className="chat-suggestion-bubbles">
                {suggestions.map((prompt, idx) => (
                  <button key={idx} className="chat-prompt-button" onClick={() => sendMessage(prompt)}>
                    {prompt}
                  </button>
                ))}
              </div>
            )}

            {quotedPreview && (
              <div className="quoted-preview">
                <span>
                  {quotedPreview.length > 100
                    ? `${quotedPreview.slice(0, 100).replace(/\s+\S*$/, '')}…`
                    : quotedPreview}
                </span>
                <button className="clear-preview" onClick={() => setQuotedPreview('')}>×</button>
              </div>
            )}

            <div className="chat-input-area">
              <textarea
                rows={2}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Ask about the article..."
                disabled={loading}
                className="chat-search-input"
              />
              <button onClick={() => sendMessage()} disabled={loading || !input.trim()}>
                {loading ? '...' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
});


export default GeminiChat;
